import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import forge from "node-forge";
import api, { SESSION_EXPIRED_EVENT } from "./axiosInstance";

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

// Role hierarchy (lab-app-auth-api.md §2): owner > admin > (signatory, staff, front_desk).
// A role passes if it is listed, or it outranks every listed role.
const ROLE_RANK = { owner: 3, admin: 2, signatory: 1, staff: 1, front_desk: 1 };
export const roleAllows = (labRole, ...allowed) => {
  if (!labRole) return false;
  if (allowed.includes(labRole)) return true;
  const mine = ROLE_RANK[labRole] ?? 0;
  return allowed.length > 0 && allowed.every((r) => mine > (ROLE_RANK[r] ?? 0));
};

// Login / switch-lab return { labStaff }, /auth/me returns a flatter shape.
// Normalise both into one user object for the app.
const fromLabStaff = (labStaff) => ({
  labUserId: labStaff._id,
  username: labStaff.username,
  phoneNumber: labStaff.phoneNumber,
  staffId: labStaff.staffId,
  staff: labStaff.staff,
  labId: labStaff.labId,
  labRole: labStaff.role,
  labs: labStaff.labs ?? [],
  mustChangePassword: !!labStaff.mustChangePassword,
});
const fromMe = (me) => ({
  staffId: me.staff?.staffId,
  staff: me.staff,
  labId: me.labId,
  labRole: me.labRole,
  labs: me.labs ?? [],
  mustChangePassword: !!me.mustChangePassword,
});

const errorMessage = (e) => e?.response?.data?.error || e?.message || "Something went wrong";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  // true until the boot-time GET /auth/me (+ refresh) has settled
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const publicKeyRef = useRef(null);

  // ── RSA-OAEP password encryption (§3) ────────────────────────────────────
  const getPublicKey = useCallback(async () => {
    if (!publicKeyRef.current) {
      const { data } = await api.get("/auth/publicKey");
      publicKeyRef.current = forge.pki.publicKeyFromPem(data.publicKey);
    }
    return publicKeyRef.current;
  }, []);

  const encryptPassword = useCallback(async (plain) => {
    const publicKey = await getPublicKey();
    return forge.util.encode64(publicKey.encrypt(plain, "RSA-OAEP"));
  }, [getPublicKey]);

  // ── GET /auth/me (§5) ────────────────────────────────────────────────────
  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      // A session from another Cohesive app is "not logged in" for the lab app
      const next = data?.type === "LabStaff" ? fromMe(data) : null;
      setUser(next);
      return next;
    } catch (e) {
      setUser(null);
      return null;
    }
  }, []);

  // ── POST /auth/lab/login (§4) ────────────────────────────────────────────
  const login = useCallback(async (username, password, labId) => {
    if (isLoggingIn) return null;
    setIsLoggingIn(true);
    try {
      const encrypted = await encryptPassword(password);
      const { data } = await api.post("/auth/lab/login", { username, password: encrypted }, { params: labId ? { labId } : undefined });
      const next = fromLabStaff(data.labStaff);
      setUser(next);
      return next;
    } catch (e) {
      // Drop the cached key so a retry re-fetches it (covers a rotated server key)
      publicKeyRef.current = null;
      throw new Error(errorMessage(e));
    } finally {
      setIsLoggingIn(false);
    }
  }, [isLoggingIn, encryptPassword]);

  // ── POST /auth/switch-lab (§7) ───────────────────────────────────────────
  const switchLab = useCallback(async (labId) => {
    try {
      const { data } = await api.post("/auth/switch-lab", { labId });
      const next = fromLabStaff(data.labStaff);
      setUser(next);
      return next;
    } catch (e) {
      throw new Error(errorMessage(e));
    }
  }, []);

  // ── POST /auth/logout (§8) ───────────────────────────────────────────────
  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.error("logout:", errorMessage(e));
    } finally {
      setUser(null);
    }
  }, []);

  // ── POST /auth/changePassword (§9) ───────────────────────────────────────
  const changePassword = useCallback(async (oldPassword, newPassword) => {
    try {
      const [encryptedOld, encryptedNew] = await Promise.all([
        encryptPassword(oldPassword),
        encryptPassword(newPassword),
      ]);
      const { data } = await api.post("/auth/changePassword", null, {
        headers: {
          "x-old-password": encryptedOld,
          "x-new-password": encryptedNew,
        },
      });
      // A forced first-login change is satisfied now (server clears the flag)
      setUser((u) => (u ? { ...u, mustChangePassword: false } : u));
      return data?.message ?? "Password changed successfully";
    } catch (e) {
      throw new Error(errorMessage(e));
    }
  }, [encryptPassword]);

  // Merge an edited Staff profile (PUT /labStaff/:id) into the session user
  const updateStaff = useCallback((staff) => {
    setUser((u) => (u ? { ...u, staff: { ...u.staff, ...staff } } : u));
  }, []);

  // ── Boot: restore session from cookies (§12) ─────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await fetchMe();
      if (!cancelled) setIsAuthLoading(false);
    })();
    return () => { cancelled = true; };
  }, [fetchMe]);

  // Refresh cookie expired mid-session → drop the user, RequireAuth redirects
  useEffect(() => {
    const onExpired = () => setUser(null);
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  const can = useCallback((...roles) => roleAllows(user?.labRole, ...roles), [user?.labRole]);

  const value = {
    user,
    labId: user?.labId ?? null,
    labRole: user?.labRole ?? null,
    labs: user?.labs ?? [],
    mustChangePassword: !!user?.mustChangePassword,
    can,
    updateStaff,
    isLoggingIn,
    isAuthLoading,
    login,
    logout,
    switchLab,
    changePassword,
    fetchMe,
    setUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
